import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Tabs, Alert, Spinner } from 'react-bootstrap';
import { FaUser, FaUserPlus, FaExchangeAlt, FaHistory, FaUndo, FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';
import api from '../services/api';

const DMTCASAModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('remitter');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  // Remitter States
  const [remitterData, setRemitterData] = useState({
    mobile: '',
    aadhar: '',
    pan: '',
    verification_type: 'FORM-60',
    kyc_remark: '',
    amount: 25
  });

  // Account Opening States
  const [accountData, setAccountData] = useState({
    mobile: '',
    aadhar: '',
    pan: '',
    verification_type: 'FORM-60',
    kyc_remark: '',
    amount: 25,
    kyc_mode: 'IRIS',
    device_name: '',
    access_type: 'SITE',
    pincode: '',
    otp: ''
  });

  // Beneficiary States
  const [beneficiaryData, setBeneficiaryData] = useState({
    mobile: '',
    bene_name: '',
    bene_account: '',
    bene_ifsc: '',
    bene_bank: '',
    otp: ''
  });

  // Transaction States
  const [transactionData, setTransactionData] = useState({
    mobile: '',
    beneid: '',
    amount: '',
    otp: ''
  });

  // Refund States
  const [refundData, setRefundData] = useState({
    transactionid: '',
    otp: ''
  });

  // Lists
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [masterData, setMasterData] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setActiveTab('remitter');
      setMessage('');
      setMessageType('info');
      setRemitterData({
        mobile: '',
        aadhar: '',
        pan: '',
        verification_type: 'FORM-60',
        kyc_remark: '',
        amount: 25
      });
      setAccountData({
        mobile: '',
        aadhar: '',
        pan: '',
        verification_type: 'FORM-60',
        kyc_remark: '',
        amount: 25,
        kyc_mode: 'IRIS',
        device_name: '',
        access_type: 'SITE',
        pincode: '',
        otp: ''
      });
      setBeneficiaryData({
        mobile: '',
        bene_name: '',
        bene_account: '',
        bene_ifsc: '',
        bene_bank: '',
        otp: ''
      });
      setTransactionData({
        mobile: '',
        beneid: '',
        amount: '',
        otp: ''
      });
      setRefundData({
        transactionid: '',
        otp: ''
      });
      setBeneficiaries([]);
      setMasterData(null);
    }
  }, [show]);

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  // Remitter Functions
  const queryRemitter = async () => {
    if (!remitterData.mobile) {
      showMessage('Please enter mobile number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/remitter/query', {
        mobile: remitterData.mobile
      });

      if (response.data.success) {
        showMessage('Remitter query successful', 'success');
        if (response.data.data?.response_code === 1) {
          showMessage('Remitter exists and is active', 'success');
        } else {
          showMessage('Remitter not found or inactive', 'warning');
        }
      } else {
        showMessage(response.data.message || 'Remitter query failed', 'danger');
      }
    } catch (error) {
      console.error('Query Remitter Error:', error);
      showMessage('Failed to query remitter', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Account Opening Functions
  const getMasterData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/dmt-casa/account-opening/master-data');

      if (response.data.success) {
        setMasterData(response.data.data);
        showMessage('Master data loaded successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to load master data', 'danger');
      }
    } catch (error) {
      console.error('Get Master Data Error:', error);
      showMessage('Failed to load master data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const checkAadhar = async () => {
    if (!accountData.aadhar) {
      showMessage('Please enter Aadhar number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/check-aadhar', {
        aadhar: accountData.aadhar
      });

      if (response.data.success) {
        showMessage('Aadhar check completed', 'success');
      } else {
        showMessage(response.data.message || 'Aadhar check failed', 'danger');
      }
    } catch (error) {
      console.error('Check Aadhar Error:', error);
      showMessage('Failed to check Aadhar', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const checkPan = async () => {
    if (!accountData.pan) {
      showMessage('Please enter PAN number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/check-pan', {
        pan: accountData.pan
      });

      if (response.data.success) {
        showMessage('PAN check completed', 'success');
      } else {
        showMessage(response.data.message || 'PAN check failed', 'danger');
      }
    } catch (error) {
      console.error('Check PAN Error:', error);
      showMessage('Failed to check PAN', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const processEkyc = async () => {
    if (!accountData.mobile || !accountData.aadhar) {
      showMessage('Please enter mobile and Aadhar number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/ekyc', {
        mobile: accountData.mobile,
        aadhar: accountData.aadhar,
        kyc_mode: accountData.kyc_mode,
        device_name: accountData.device_name,
        access_type: accountData.access_type
      });

      if (response.data.success) {
        showMessage('E-KYC process completed', 'success');
      } else {
        showMessage(response.data.message || 'E-KYC process failed', 'danger');
      }
    } catch (error) {
      console.error('E-KYC Error:', error);
      showMessage('Failed to process E-KYC', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const checkPincode = async () => {
    if (!accountData.pincode) {
      showMessage('Please enter pincode', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/check-pincode', {
        pincode: accountData.pincode
      });

      if (response.data.success) {
        showMessage('Pincode check completed', 'success');
      } else {
        showMessage(response.data.message || 'Pincode check failed', 'danger');
      }
    } catch (error) {
      console.error('Check Pincode Error:', error);
      showMessage('Failed to check pincode', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const getOtp = async () => {
    if (!accountData.mobile) {
      showMessage('Please enter mobile number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/get-otp', {
        mobile: accountData.mobile
      });

      if (response.data.success) {
        showMessage('OTP sent successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to send OTP', 'danger');
      }
    } catch (error) {
      console.error('Get OTP Error:', error);
      showMessage('Failed to send OTP', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const submitAccount = async () => {
    if (!accountData.mobile || !accountData.aadhar) {
      showMessage('Please enter mobile and Aadhar number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/submit', {
        mobile: accountData.mobile,
        aadhar: accountData.aadhar,
        pan: accountData.pan,
        verification_type: accountData.verification_type,
        kyc_remark: accountData.kyc_remark,
        amount: accountData.amount
      });

      if (response.data.success) {
        showMessage('Account submitted successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Account submission failed', 'danger');
      }
    } catch (error) {
      console.error('Submit Account Error:', error);
      showMessage('Failed to submit account', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const submitAccountRevision = async () => {
    if (!accountData.mobile || !accountData.aadhar) {
      showMessage('Please enter mobile and Aadhar number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/account-opening/revision', {
        mobile: accountData.mobile,
        aadhar: accountData.aadhar,
        pan: accountData.pan,
        verification_type: accountData.verification_type,
        kyc_remark: accountData.kyc_remark,
        amount: accountData.amount
      });

      if (response.data.success) {
        showMessage('Account revision submitted successfully', 'success');
      } else {
        showMessage(response.data.message || 'Account revision submission failed', 'danger');
      }
    } catch (error) {
      console.error('Submit Account Revision Error:', error);
      showMessage('Failed to submit account revision', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Beneficiary Functions
  const sendBeneficiaryOtp = async () => {
    if (!beneficiaryData.mobile) {
      showMessage('Please enter mobile number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/beneficiary/send-otp', {
        mobile: beneficiaryData.mobile
      });

      if (response.data.success) {
        showMessage('Beneficiary OTP sent successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to send beneficiary OTP', 'danger');
      }
    } catch (error) {
      console.error('Send Beneficiary OTP Error:', error);
      showMessage('Failed to send beneficiary OTP', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const addBeneficiary = async () => {
    if (!beneficiaryData.mobile || !beneficiaryData.bene_name || !beneficiaryData.bene_account || !beneficiaryData.bene_ifsc || !beneficiaryData.bene_bank || !beneficiaryData.otp) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/beneficiary/add', {
        mobile: beneficiaryData.mobile,
        bene_name: beneficiaryData.bene_name,
        bene_account: beneficiaryData.bene_account,
        bene_ifsc: beneficiaryData.bene_ifsc,
        bene_bank: beneficiaryData.bene_bank,
        otp: beneficiaryData.otp
      });

      if (response.data.success) {
        showMessage('Beneficiary added successfully', 'success');
        setBeneficiaryData({
          mobile: '',
          bene_name: '',
          bene_account: '',
          bene_ifsc: '',
          bene_bank: '',
          otp: ''
        });
        getBeneficiaryList();
      } else {
        showMessage(response.data.message || 'Failed to add beneficiary', 'danger');
      }
    } catch (error) {
      console.error('Add Beneficiary Error:', error);
      showMessage('Failed to add beneficiary', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const getBeneficiaryList = async () => {
    if (!beneficiaryData.mobile) {
      showMessage('Please enter mobile number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/beneficiary/list', {
        mobile: beneficiaryData.mobile
      });

      if (response.data.success) {
        setBeneficiaries(response.data.data?.beneficiaries || []);
        showMessage('Beneficiary list loaded successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to get beneficiary list', 'danger');
      }
    } catch (error) {
      console.error('Get Beneficiary List Error:', error);
      showMessage('Failed to get beneficiary list', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const deleteBeneficiary = async (beneId) => {
    if (!window.confirm('Are you sure you want to delete this beneficiary?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/beneficiary/delete', {
        beneid: beneId
      });

      if (response.data.success) {
        showMessage('Beneficiary deleted successfully', 'success');
        getBeneficiaryList();
      } else {
        showMessage(response.data.message || 'Failed to delete beneficiary', 'danger');
      }
    } catch (error) {
      console.error('Delete Beneficiary Error:', error);
      showMessage('Failed to delete beneficiary', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const verifyBeneficiary = async (beneId) => {
    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/beneficiary/verify', {
        beneid: beneId,
        bene_name: beneficiaryData.bene_name,
        bene_account: beneficiaryData.bene_account,
        bene_ifsc: beneficiaryData.bene_ifsc
      });

      if (response.data.success) {
        showMessage('Beneficiary verification completed', 'success');
      } else {
        showMessage(response.data.message || 'Beneficiary verification failed', 'danger');
      }
    } catch (error) {
      console.error('Verify Beneficiary Error:', error);
      showMessage('Failed to verify beneficiary', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Transaction Functions
  const sendTransactionOtp = async () => {
    if (!transactionData.mobile || !transactionData.beneid || !transactionData.amount) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    if (parseFloat(transactionData.amount) > 49000) {
      showMessage('Amount cannot exceed Rs 49,000', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/transaction/send-otp', {
        mobile: transactionData.mobile,
        beneid: transactionData.beneid,
        amount: parseFloat(transactionData.amount)
      });

      if (response.data.success) {
        showMessage('Transaction OTP sent successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to send transaction OTP', 'danger');
      }
    } catch (error) {
      console.error('Send Transaction OTP Error:', error);
      showMessage('Failed to send transaction OTP', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const verifyTransactionOtp = async () => {
    if (!transactionData.mobile || !transactionData.beneid || !transactionData.amount || !transactionData.otp) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/transaction/verify-otp', {
        mobile: transactionData.mobile,
        beneid: transactionData.beneid,
        amount: parseFloat(transactionData.amount),
        otp: transactionData.otp
      });

      if (response.data.success) {
        showMessage('Transaction completed successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Transaction failed', 'danger');
      }
    } catch (error) {
      console.error('Verify Transaction OTP Error:', error);
      showMessage('Failed to complete transaction', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const checkTransactionStatus = async () => {
    if (!refundData.transactionid) {
      showMessage('Please enter transaction ID', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/transaction/status', {
        transactionid: refundData.transactionid
      });

      if (response.data.success) {
        showMessage('Transaction status retrieved successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to get transaction status', 'danger');
      }
    } catch (error) {
      console.error('Check Transaction Status Error:', error);
      showMessage('Failed to check transaction status', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Refund Functions
  const sendRefundOtp = async () => {
    if (!refundData.transactionid) {
      showMessage('Please enter transaction ID', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/refund/send-otp', {
        transactionid: refundData.transactionid
      });

      if (response.data.success) {
        showMessage('Refund OTP sent successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to send refund OTP', 'danger');
      }
    } catch (error) {
      console.error('Send Refund OTP Error:', error);
      showMessage('Failed to send refund OTP', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const claimRefund = async () => {
    if (!refundData.transactionid || !refundData.otp) {
      showMessage('Please enter transaction ID and OTP', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-casa/refund/claim', {
        transactionid: refundData.transactionid,
        otp: refundData.otp
      });

      if (response.data.success) {
        showMessage('Refund claimed successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Refund claim failed', 'danger');
      }
    } catch (error) {
      console.error('Claim Refund Error:', error);
      showMessage('Failed to claim refund', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUser className="me-2" />
          DMT CASA - Complete Money Transfer System
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && (
          <Alert variant={messageType} className="mb-3">
            {message}
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* Remitter Tab */}
          <Tab eventKey="remitter" title="Remitter">
            <div className="p-3">
              <h5><FaUser className="me-2" />Remitter Management</h5>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={remitterData.mobile}
                      onChange={(e) => setRemitterData({...remitterData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button 
                    variant="primary" 
                    onClick={queryRemitter}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Query Remitter'}
                  </Button>
                </Col>
              </Row>
            </div>
          </Tab>

          {/* Account Opening Tab */}
          <Tab eventKey="account" title="Account Opening">
            <div className="p-3">
              <h5><FaUserPlus className="me-2" />Account Opening Process</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={accountData.mobile}
                      onChange={(e) => setAccountData({...accountData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Aadhar Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 12-digit Aadhar number"
                      value={accountData.aadhar}
                      onChange={(e) => setAccountData({...accountData, aadhar: e.target.value})}
                      maxLength={12}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>PAN Number (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter PAN number"
                      value={accountData.pan}
                      onChange={(e) => setAccountData({...accountData, pan: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Verification Type</Form.Label>
                    <Form.Select
                      value={accountData.verification_type}
                      onChange={(e) => setAccountData({...accountData, verification_type: e.target.value})}
                    >
                      <option value="FORM-60">FORM-60</option>
                      <option value="PAN">PAN</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>KYC Mode</Form.Label>
                    <Form.Select
                      value={accountData.kyc_mode}
                      onChange={(e) => setAccountData({...accountData, kyc_mode: e.target.value})}
                    >
                      <option value="IRIS">IRIS</option>
                      <option value="FINGER">FINGER</option>
                      <option value="FACE">FACE</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Access Type</Form.Label>
                    <Form.Select
                      value={accountData.access_type}
                      onChange={(e) => setAccountData({...accountData, access_type: e.target.value})}
                    >
                      <option value="SITE">SITE</option>
                      <option value="SDK">SDK</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit pincode"
                      value={accountData.pincode}
                      onChange={(e) => setAccountData({...accountData, pincode: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Amount (Rs 25)</Form.Label>
                    <Form.Control
                      type="number"
                      value={accountData.amount}
                      onChange={(e) => setAccountData({...accountData, amount: parseFloat(e.target.value)})}
                      min="25"
                      max="25"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>KYC Remark</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter KYC remark"
                      value={accountData.kyc_remark}
                      onChange={(e) => setAccountData({...accountData, kyc_remark: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button variant="info" onClick={getMasterData} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Get Master Data'}
                </Button>
                <Button variant="warning" onClick={checkAadhar} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Check Aadhar'}
                </Button>
                <Button variant="warning" onClick={checkPan} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Check PAN'}
                </Button>
                <Button variant="primary" onClick={processEkyc} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'E-KYC Process'}
                </Button>
                <Button variant="secondary" onClick={checkPincode} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Check Pincode'}
                </Button>
                <Button variant="success" onClick={getOtp} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Get OTP'}
                </Button>
              </div>

              <div className="d-flex gap-2">
                <Button variant="primary" onClick={submitAccount} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Submit Account'}
                </Button>
                <Button variant="warning" onClick={submitAccountRevision} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Submit Revision'}
                </Button>
              </div>
            </div>
          </Tab>

          {/* Beneficiary Tab */}
          <Tab eventKey="beneficiary" title="Beneficiary">
            <div className="p-3">
              <h5><FaUserPlus className="me-2" />Beneficiary Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={beneficiaryData.mobile}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={sendBeneficiaryOtp}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Send OTP'}
                  </Button>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Beneficiary Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter beneficiary name"
                      value={beneficiaryData.bene_name}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account number"
                      value={beneficiaryData.bene_account}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_account: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>IFSC Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter IFSC code"
                      value={beneficiaryData.bene_ifsc}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_ifsc: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter bank name"
                      value={beneficiaryData.bene_bank}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_bank: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>OTP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={beneficiaryData.otp}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, otp: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button 
                    variant="success" 
                    onClick={addBeneficiary}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Add Beneficiary'}
                  </Button>
                </Col>
              </Row>

              <div className="d-flex gap-2 mb-3">
                <Button variant="info" onClick={getBeneficiaryList} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Get Beneficiary List'}
                </Button>
              </div>

              {beneficiaries.length > 0 && (
                <div className="mt-3">
                  <h6>Beneficiary List</h6>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Account</th>
                          <th>IFSC</th>
                          <th>Bank</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {beneficiaries.map((bene, index) => (
                          <tr key={index}>
                            <td>{bene.bene_name}</td>
                            <td>{bene.bene_account}</td>
                            <td>{bene.bene_ifsc}</td>
                            <td>{bene.bene_bank}</td>
                            <td>
                              <Button 
                                variant="info" 
                                size="sm" 
                                onClick={() => verifyBeneficiary(bene.beneid)}
                                disabled={loading}
                                className="me-2"
                              >
                                Verify
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => deleteBeneficiary(bene.beneid)}
                                disabled={loading}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </Tab>

          {/* Transaction Tab */}
          <Tab eventKey="transaction" title="Transaction">
            <div className="p-3">
              <h5><FaExchangeAlt className="me-2" />Transaction Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={transactionData.mobile}
                      onChange={(e) => setTransactionData({...transactionData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Beneficiary ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter beneficiary ID"
                      value={transactionData.beneid}
                      onChange={(e) => setTransactionData({...transactionData, beneid: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Amount (Max Rs 49,000)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter amount"
                      value={transactionData.amount}
                      onChange={(e) => setTransactionData({...transactionData, amount: e.target.value})}
                      min="1"
                      max="49000"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>OTP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={transactionData.otp}
                      onChange={(e) => setTransactionData({...transactionData, otp: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button 
                  variant="primary" 
                  onClick={sendTransactionOtp}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Send OTP'}
                </Button>
                <Button 
                  variant="success" 
                  onClick={verifyTransactionOtp}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Verify OTP & Transfer'}
                </Button>
              </div>
            </div>
          </Tab>

          {/* Status Tab */}
          <Tab eventKey="status" title="Status">
            <div className="p-3">
              <h5><FaHistory className="me-2" />Transaction Status</h5>
              
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Transaction ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter transaction ID"
                      value={refundData.transactionid}
                      onChange={(e) => setRefundData({...refundData, transactionid: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={checkTransactionStatus}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Check Status'}
                  </Button>
                </Col>
              </Row>
            </div>
          </Tab>

          {/* Refund Tab */}
          <Tab eventKey="refund" title="Refund">
            <div className="p-3">
              <h5><FaUndo className="me-2" />Refund Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Transaction ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter transaction ID"
                      value={refundData.transactionid}
                      onChange={(e) => setRefundData({...refundData, transactionid: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>OTP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={refundData.otp}
                      onChange={(e) => setRefundData({...refundData, otp: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button 
                  variant="warning" 
                  onClick={sendRefundOtp}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Send Refund OTP'}
                </Button>
                <Button 
                  variant="danger" 
                  onClick={claimRefund}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Claim Refund'}
                </Button>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DMTCASAModal;
